import { ObjectId } from "@novorender/data-js-api";
import { createContext, Dispatch, MutableRefObject } from "react";

import { SelectionSet } from "utils/selectionSet";

// Ids are held once, in an immutable SelectionSet (sorted Uint32Array, ~4
// bytes/id) — see the note in contexts/highlighted/reducer.ts. The basket can
// mirror a full-scene selection (1M+ ids), so the previous Record + array pair
// was one of the largest remaining app-side allocations. `idArr` is the set's
// sorted backing array; it MUST NOT be mutated.

export const initialState = {
    ids: SelectionSet.EMPTY,
    idArr: SelectionSet.EMPTY.sortedIds,
};

export type State = typeof initialState;

enum ActionTypes {
    Add,
    Remove,
    Set,
}

function add(ids: Iterable<ObjectId>) {
    return {
        type: ActionTypes.Add as const,
        ids,
    };
}

function remove(ids: Iterable<ObjectId>) {
    return {
        type: ActionTypes.Remove as const,
        ids,
    };
}

function set(ids: Iterable<ObjectId>) {
    return {
        type: ActionTypes.Set as const,
        ids,
    };
}

export const actions = { add, remove, set };

type Actions = ReturnType<(typeof actions)[keyof typeof actions]>;
export type DispatchSelectionBasket = Dispatch<Actions>;
export type LazyState = MutableRefObject<State>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const StateContext = createContext<State>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyStateContext = createContext<LazyState>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DispatchContext = createContext<DispatchSelectionBasket>(undefined as any);

function withIds(ids: SelectionSet): State {
    return { ids, idArr: ids.sortedIds };
}

export function reducer(state: State, action: Actions): State {
    switch (action.type) {
        case ActionTypes.Add: {
            return withIds(state.ids.withAdded(action.ids));
        }
        case ActionTypes.Remove: {
            return withIds(state.ids.withRemoved(action.ids));
        }
        case ActionTypes.Set: {
            return withIds(SelectionSet.fromIds(action.ids));
        }
        default: {
            throw new Error(`Unhandled action type`);
        }
    }
}
